import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const OVERLAY: CSSProperties = {
  position: "fixed", inset: 0, zIndex: 50,
  display: "flex", alignItems: "center", justifyContent: "center",
  background: "rgba(0,0,0,0.6)",
};
const SHEET: CSSProperties = {
  background: "var(--d-ink-2)", borderRadius: 22, padding: 24,
  maxWidth: 420, width: "calc(100% - 48px)",
  display: "flex", flexDirection: "column", gap: 16,
  border: "1px solid var(--d-line)",
};
const TITLE: CSSProperties = {
  fontFamily: "var(--font-display)", fontSize: 20, color: "var(--d-fg)",
};
const BODY: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 14, color: "var(--d-fg-muted)",
};
const ROW: CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 8 };
const BTN_BASE: CSSProperties = {
  padding: "8px 16px", borderRadius: 999, fontFamily: "var(--font-body)",
  border: "1px solid var(--d-line)", background: "transparent",
  color: "var(--d-fg)", cursor: "pointer",
};

export function ConfirmModal({
  open, title, body, confirmLabel, cancelLabel, destructive, onConfirm, onClose,
}: Props): JSX.Element | null {
  const { t } = useTranslation("admin");
  if (!open) return null;
  const confirmStyle: CSSProperties = {
    ...BTN_BASE,
    background: destructive ? "var(--d-ember)" : "var(--d-violet)",
    color: "#fff", borderColor: "transparent",
  };
  return (
    <div role="dialog" aria-modal="true" style={OVERLAY} onClick={onClose}>
      <div style={SHEET} onClick={(e) => e.stopPropagation()}>
        <div style={TITLE}>{title}</div>
        <div style={BODY}>{body}</div>
        <div style={ROW}>
          <button style={BTN_BASE} onClick={onClose}>
            {cancelLabel ?? t("common.cancel")}
          </button>
          <button style={confirmStyle} onClick={onConfirm}>
            {confirmLabel ?? t("common.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
